// v2.8 F4 (AD4): the public live-score page at /s/<token>. No login: anyone
// with the link sees program name, opponent, score, periods, status and the
// top scorers (first name + last initial). Polls every 15 s while the game
// is live and stops on final, on a cancelled game, when the scoreboard is
// switched off (403) or when the page goes away.
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { scoreboardAPI } from '../utils/api';
import { formatEventDate, formatEventTime } from '../utils/eventFormat';

export const POLL_MS = 15000;

const Scoreboard = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await scoreboardAPI.public(token);
        if (!active) return;
        setData(res.data);
        setError('');
        // Scheduled and live both refresh; final and cancelled games are done (X-18).
        if (res.data.status !== 'final' && res.data.gameStatus !== 'cancelled') timer.current = setTimeout(load, POLL_MS);
      } catch (err) {
        if (!active) return;
        const status = err.response?.status;
        // X-18: 404 (bad link) and 403 (scoreboard switched off) are final answers; only other failures retry.
        if (status === 404) setError('This scoreboard link is no longer valid.');
        else if (status === 403) setError('Live scores are not available for this game right now.');
        else {
          setError('Could not load the score. Trying again…');
          timer.current = setTimeout(load, POLL_MS);
        }
      }
    };
    load();
    return () => { active = false; if (timer.current) clearTimeout(timer.current); };
  }, [token]);

  const status = data?.status || 'scheduled';
  const cancelled = data?.gameStatus === 'cancelled';
  const postponed = data?.gameStatus === 'postponed';
  const leading = data ? (data.us > data.them ? 'us' : data.us < data.them ? 'them' : null) : null;

  return (
    <div style={styles.page}>
      <Helmet>
        <title>{data ? `${data.programName} ${data.us}–${data.them} ${data.opponent} | HoopHero` : 'Live score | HoopHero'}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <style>{`@keyframes hh-pulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }`}</style>
      <div style={styles.card}>
        {error && !data ? (
          <div style={styles.message} role="alert">{error}</div>
        ) : !data ? (
          <div style={styles.message}>Loading score…</div>
        ) : (
          <>
            <div style={styles.topRow}>
              <div style={styles.program}>{data.programName}</div>
              {cancelled ? <span style={{ ...styles.pill, ...styles.pillCancelled }}>CANCELLED</span> : (
                <>
                  {status === 'live' && <span style={{ ...styles.pill, ...styles.pillLive }}><span style={styles.dot} aria-hidden="true" />LIVE</span>}
                  {status === 'final' && <span style={{ ...styles.pill, ...styles.pillFinal }}>FINAL</span>}
                  {status === 'scheduled' && <span style={{ ...styles.pill, ...styles.pillScheduled }}>{postponed ? 'POSTPONED' : 'NOT STARTED'}</span>}
                </>
              )}
            </div>
            <div style={styles.when}>
              {data.gameDate ? formatEventDate(data.gameDate).short : ''}{data.gameTime ? ` · ${formatEventTime(data.gameTime)}` : ''}
            </div>
            {data.location && <div style={styles.when} data-testid="score-location">📍 {data.location}</div>}

            <div style={styles.scoreRow} aria-label={`${data.programName} ${data.us}, ${data.opponent} ${data.them}`}>
              <div style={styles.side}>
                <div style={styles.teamName}>{data.programName}</div>
                <div style={{ ...styles.score, color: leading === 'us' ? '#C2460B' : '#0D1117' }} data-testid="score-us">{data.us}</div>
              </div>
              <div style={styles.vs}>–</div>
              <div style={styles.side}>
                <div style={styles.teamName}>{data.opponent}</div>
                <div style={{ ...styles.score, color: leading === 'them' ? '#C2460B' : '#0D1117' }} data-testid="score-them">{data.them}</div>
              </div>
            </div>

            {data.periods?.length > 0 && (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={{ ...styles.th, textAlign: 'left' }}>Period</th>
                      {data.periods.map((p, i) => <th key={i} style={styles.th}>{p.label}</th>)}
                      <th style={styles.th}>T</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ ...styles.td, textAlign: 'left', fontWeight: 700 }}>{data.programName}</td>
                      {data.periods.map((p, i) => <td key={i} style={styles.td}>{p.us}</td>)}
                      <td style={{ ...styles.td, fontWeight: 800 }}>{data.us}</td>
                    </tr>
                    <tr>
                      <td style={{ ...styles.td, textAlign: 'left', fontWeight: 700 }}>{data.opponent}</td>
                      {data.periods.map((p, i) => <td key={i} style={styles.td}>{p.them}</td>)}
                      <td style={{ ...styles.td, fontWeight: 800 }}>{data.them}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {data.topScorers?.length > 0 && (
              <div style={styles.scorers}>
                <div style={styles.sectionTitle}>Top scorers</div>
                <ol style={styles.list}>
                  {data.topScorers.map((s, i) => (
                    <li key={`${s.name}-${i}`} style={styles.scorer}>
                      <span>{s.name}</span>
                      <span style={styles.points}>{s.points}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div style={styles.footer}>
              {cancelled ? 'This game was cancelled. The score will not update.' : status === 'live' ? 'Updates every 15 seconds.' : status === 'final' ? 'Final score.' : postponed ? 'This game was postponed. Check back when it is rescheduled.' : 'The score appears here once the game starts.'}
              {error ? ` ${error}` : ''}
            </div>
          </>
        )}
        <a href="/" style={styles.brand}>Powered by HoopHero</a>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', background: '#0D1117', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px 12px 40px', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  card: { background: '#fff', borderRadius: '16px', padding: '20px 18px', width: '100%', maxWidth: '460px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' },
  program: { fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: 800, color: '#0D1117' },
  when: { fontSize: '12.5px', color: '#5A6070', marginTop: '4px' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 800, letterSpacing: '1px', padding: '4px 10px', borderRadius: '999px' },
  pillLive: { background: '#FDEAEA', color: '#C93535' },
  pillFinal: { background: '#ECEEF3', color: '#0D1117' },
  pillScheduled: { background: '#E6EFFC', color: '#1A5FD4' },
  pillCancelled: { background: '#FDEAEA', color: '#C93535' },
  dot: { width: '8px', height: '8px', borderRadius: '50%', background: '#C93535', animation: 'hh-pulse 1.4s ease-in-out infinite' },
  scoreRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', margin: '22px 0 10px' },
  side: { flex: 1, textAlign: 'center', minWidth: 0 },
  teamName: { fontSize: '13px', fontWeight: 700, color: '#5A6070', textTransform: 'uppercase', letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  score: { fontFamily: 'Georgia, serif', fontSize: '64px', fontWeight: 800, lineHeight: 1.1 },
  vs: { fontSize: '28px', color: '#9AA0B0' },
  tableWrap: { overflowX: 'auto', marginTop: '8px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { padding: '6px 8px', color: '#9AA0B0', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(0,0,0,0.08)', textAlign: 'center' },
  td: { padding: '7px 8px', textAlign: 'center', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#0D1117' },
  scorers: { marginTop: '18px' },
  sectionTitle: { fontSize: '11px', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: '#9AA0B0', marginBottom: '6px' },
  list: { listStyle: 'none', margin: 0, padding: 0 },
  scorer: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,0.05)', fontSize: '14px', color: '#0D1117' },
  points: { fontWeight: 800, color: '#C2460B' },
  footer: { fontSize: '12px', color: '#9AA0B0', marginTop: '16px' },
  message: { padding: '30px 0', textAlign: 'center', color: '#5A6070', fontSize: '14px' },
  brand: { display: 'block', marginTop: '14px', fontSize: '11.5px', color: '#9AA0B0', textDecoration: 'none', textAlign: 'center' },
};

export default Scoreboard;
